import type { ControlState, InputSnapshot, PlayerNumber } from '../game/types';

export type GamepadTarget = 'off' | 'p1' | 'p2' | 'both';
export type GamepadSlot = 'auto' | '0' | '1' | '2' | '3';
export type TouchTarget = 'off' | 'p1' | 'p2';
export type KeyboardMoveScheme = 'arrows' | 'wasd' | 'tfgh' | 'ijkl' | 'numpad';

export interface InputBindings {
  p1FireCode: string;
  p2FireCode: string;
  p3FireCode: string;
  p4FireCode: string;
  p1MoveScheme: KeyboardMoveScheme;
  p2MoveScheme: KeyboardMoveScheme;
  p3MoveScheme: KeyboardMoveScheme;
  p4MoveScheme: KeyboardMoveScheme;
  p1KeyboardEnabled: boolean;
  p2KeyboardEnabled: boolean;
  p3KeyboardEnabled: boolean;
  p4KeyboardEnabled: boolean;
  gamepadTarget: GamepadTarget;
  p1GamepadSlot: GamepadSlot;
  p2GamepadSlot: GamepadSlot;
  p3GamepadEnabled: boolean;
  p4GamepadEnabled: boolean;
  p3GamepadSlot: GamepadSlot;
  p4GamepadSlot: GamepadSlot;
  touchTarget: TouchTarget;
}

const EMPTY_CONTROL: ControlState = {
  up: false,
  down: false,
  left: false,
  right: false,
  fire: false,
};

export class InputManager {
  private keys = new Set<string>();
  private pauseLatch = false;
  private gamepadPauseButtons = new Set<number>();
  private touchControls: ControlState = { ...EMPTY_CONTROL };
  private bindings: InputBindings = {
    p1FireCode: 'Space',
    p2FireCode: 'KeyJ',
    p3FireCode: 'KeyY',
    p4FireCode: 'KeyO',
    p1MoveScheme: 'arrows',
    p2MoveScheme: 'wasd',
    p3MoveScheme: 'tfgh',
    p4MoveScheme: 'ijkl',
    p1KeyboardEnabled: true,
    p2KeyboardEnabled: true,
    p3KeyboardEnabled: false,
    p4KeyboardEnabled: false,
    gamepadTarget: 'off',
    p1GamepadSlot: 'auto',
    p2GamepadSlot: 'auto',
    p3GamepadEnabled: false,
    p4GamepadEnabled: false,
    p3GamepadSlot: 'auto',
    p4GamepadSlot: 'auto',
    touchTarget: 'off',
  };

  constructor(target: Window) {
    target.addEventListener('keydown', (event) => {
      if (isEditableTarget(event.target)) return;
      this.keys.add(event.code);
      this.keys.add(event.key.toLowerCase());
      if (this.isHandled(event.code)) event.preventDefault();
      if (event.code === 'Escape') this.pauseLatch = true;
    });
    target.addEventListener('keyup', (event) => {
      this.keys.delete(event.code);
      this.keys.delete(event.key.toLowerCase());
      if (isEditableTarget(event.target)) return;
      if (this.isHandled(event.code)) event.preventDefault();
    });
    target.addEventListener('blur', () => this.keys.clear());
  }

  setBindings(bindings: Partial<InputBindings>): void {
    this.bindings = { ...this.bindings, ...bindings };
  }

  setTouchControls(controls: ControlState): void {
    this.touchControls = { ...EMPTY_CONTROL, ...controls };
  }

  isFireCode(code: string): boolean {
    return [
      this.bindings.p1FireCode,
      this.bindings.p2FireCode,
      this.bindings.p3FireCode,
      this.bindings.p4FireCode,
    ].includes(code);
  }

  snapshot(): InputSnapshot {
    const gamepads = this.readGamepads();
    const pausePressed = this.pauseLatch || this.consumeGamepadPause(gamepads.map((entry) => entry.pad));
    this.pauseLatch = false;
    const p1 = this.bindings.p1KeyboardEnabled
      ? {
          ...keyboardMoveState(this.keys, this.bindings.p1MoveScheme),
          fire: this.hasCode(this.bindings.p1FireCode),
        }
      : { ...EMPTY_CONTROL };
    const p2 = this.bindings.p2KeyboardEnabled
      ? {
          ...keyboardMoveState(this.keys, this.bindings.p2MoveScheme),
          fire: this.hasCode(this.bindings.p2FireCode),
        }
      : { ...EMPTY_CONTROL };
    const p3 = this.bindings.p3KeyboardEnabled
      ? {
          ...keyboardMoveState(this.keys, this.bindings.p3MoveScheme),
          fire: this.hasCode(this.bindings.p3FireCode),
        }
      : { ...EMPTY_CONTROL };
    const p4 = this.bindings.p4KeyboardEnabled
      ? {
          ...keyboardMoveState(this.keys, this.bindings.p4MoveScheme),
          fire: this.hasCode(this.bindings.p4FireCode),
        }
      : { ...EMPTY_CONTROL };
    const gamepadPlayers = this.activeGamepadPlayers();
    const assignments = this.resolveGamepadAssignments(gamepads, gamepadPlayers);
    const controlsByPlayer: Record<PlayerNumber, ControlState> = { 1: p1, 2: p2, 3: p3, 4: p4 };
    for (const [player, gamepad] of assignments) mergeControls(controlsByPlayer[player], gamepad.control);
    if (this.bindings.touchTarget === 'p1') mergeControls(p1, this.touchControls);
    if (this.bindings.touchTarget === 'p2') mergeControls(p2, this.touchControls);
    return {
      p1,
      p2,
      p3,
      p4,
      pausePressed,
    };
  }

  idleSnapshot(): InputSnapshot {
    return {
      p1: { ...EMPTY_CONTROL },
      p2: { ...EMPTY_CONTROL },
      p3: { ...EMPTY_CONTROL },
      p4: { ...EMPTY_CONTROL },
      pausePressed: false,
    };
  }

  private isHandled(code: string): boolean {
    return [
      'Escape',
      this.bindings.p1FireCode,
      this.bindings.p2FireCode,
      this.bindings.p3FireCode,
      this.bindings.p4FireCode,
      ...moveCodesForScheme(this.bindings.p1MoveScheme),
      ...moveCodesForScheme(this.bindings.p2MoveScheme),
      ...moveCodesForScheme(this.bindings.p3MoveScheme),
      ...moveCodesForScheme(this.bindings.p4MoveScheme),
    ].includes(code);
  }

  private hasCode(code: string): boolean {
    return this.keys.has(code);
  }

  private readGamepads(): Array<{ pad: Gamepad; control: ControlState }> {
    if (!this.activeGamepadPlayers().length) return [];
    if (typeof navigator === 'undefined') return [];
    const pads = navigator.getGamepads?.();
    const connected = pads ? Array.from(pads).filter((candidate): candidate is Gamepad => Boolean(candidate?.connected)) : [];
    return connected.map((pad) => ({ pad, control: this.gamepadControlState(pad) }));
  }

  private activeGamepadPlayers(): Array<{ player: PlayerNumber; slot: GamepadSlot }> {
    const players: Array<{ player: PlayerNumber; slot: GamepadSlot }> = [];
    if (this.bindings.gamepadTarget === 'p1' || this.bindings.gamepadTarget === 'both') players.push({ player: 1, slot: this.bindings.p1GamepadSlot });
    if (this.bindings.gamepadTarget === 'p2' || this.bindings.gamepadTarget === 'both') players.push({ player: 2, slot: this.bindings.p2GamepadSlot });
    if (this.bindings.p3GamepadEnabled) players.push({ player: 3, slot: this.bindings.p3GamepadSlot });
    if (this.bindings.p4GamepadEnabled) players.push({ player: 4, slot: this.bindings.p4GamepadSlot });
    return players;
  }

  private resolveGamepadAssignments(
    gamepads: Array<{ pad: Gamepad; control: ControlState }>,
    players: Array<{ player: PlayerNumber; slot: GamepadSlot }>,
  ): Map<PlayerNumber, { pad: Gamepad; control: ControlState }> {
    const assignments = new Map<PlayerNumber, { pad: Gamepad; control: ControlState }>();
    const used = new Set<number>();
    for (const entry of players) {
      if (entry.slot === 'auto') continue;
      const pad = this.gamepadForSlot(gamepads, entry.slot);
      if (!pad || used.has(pad.pad.index)) continue;
      assignments.set(entry.player, pad);
      used.add(pad.pad.index);
    }
    for (const entry of players) {
      if (assignments.has(entry.player)) continue;
      const pad = this.firstAvailableGamepad(gamepads, used);
      if (!pad) continue;
      assignments.set(entry.player, pad);
      used.add(pad.pad.index);
    }
    return assignments;
  }

  private gamepadForSlot(gamepads: Array<{ pad: Gamepad; control: ControlState }>, slot: GamepadSlot): { pad: Gamepad; control: ControlState } | null {
    if (slot === 'auto') return null;
    const index = Number(slot);
    return gamepads.find((entry) => entry.pad.index === index) ?? gamepads[index] ?? null;
  }

  private firstAvailableGamepad(gamepads: Array<{ pad: Gamepad; control: ControlState }>, reservedIndexes: Set<number>): { pad: Gamepad; control: ControlState } | null {
    return gamepads.find((entry) => !reservedIndexes.has(entry.pad.index)) ?? null;
  }

  private gamepadControlState(pad: Gamepad): ControlState {
    const axisX = pad.axes[0] ?? 0;
    const axisY = pad.axes[1] ?? 0;
    const deadzone = 0.38;
    const horizontal = Math.abs(axisX) > Math.abs(axisY) && Math.abs(axisX) > deadzone;
    const vertical = Math.abs(axisY) >= Math.abs(axisX) && Math.abs(axisY) > deadzone;
    return {
      up: vertical && axisY < 0 || Boolean(pad.buttons[12]?.pressed),
      down: vertical && axisY > 0 || Boolean(pad.buttons[13]?.pressed),
      left: horizontal && axisX < 0 || Boolean(pad.buttons[14]?.pressed),
      right: horizontal && axisX > 0 || Boolean(pad.buttons[15]?.pressed),
      fire: Boolean(pad.buttons[0]?.pressed || pad.buttons[1]?.pressed || pad.buttons[7]?.pressed),
    };
  }

  private consumeGamepadPause(pads: Gamepad[]): boolean {
    let pressedNow = false;
    const active = new Set<number>();
    for (const pad of pads) {
      const pressed = Boolean(pad.buttons[9]?.pressed || pad.buttons[8]?.pressed);
      if (!pressed) continue;
      active.add(pad.index);
      if (!this.gamepadPauseButtons.has(pad.index)) pressedNow = true;
    }
    this.gamepadPauseButtons = active;
    return pressedNow;
  }
}

function mergeControls(target: ControlState, source: ControlState): void {
  target.up ||= source.up;
  target.down ||= source.down;
  target.left ||= source.left;
  target.right ||= source.right;
  target.fire ||= source.fire;
}

function keyboardMoveState(keys: Set<string>, scheme: KeyboardMoveScheme): ControlState {
  if (scheme === 'wasd') {
    return {
      up: keys.has('KeyW') || keys.has('w'),
      down: keys.has('KeyS') || keys.has('s'),
      left: keys.has('KeyA') || keys.has('a'),
      right: keys.has('KeyD') || keys.has('d'),
      fire: false,
    };
  }
  if (scheme === 'tfgh') {
    return {
      up: keys.has('KeyT') || keys.has('t'),
      down: keys.has('KeyG') || keys.has('g'),
      left: keys.has('KeyF') || keys.has('f'),
      right: keys.has('KeyH') || keys.has('h'),
      fire: false,
    };
  }
  if (scheme === 'ijkl') {
    return {
      up: keys.has('KeyI') || keys.has('i'),
      down: keys.has('KeyK') || keys.has('k'),
      left: keys.has('KeyJ') || keys.has('j'),
      right: keys.has('KeyL') || keys.has('l'),
      fire: false,
    };
  }
  if (scheme === 'numpad') {
    return {
      up: keys.has('Numpad8'),
      down: keys.has('Numpad5') || keys.has('Numpad2'),
      left: keys.has('Numpad4'),
      right: keys.has('Numpad6'),
      fire: false,
    };
  }
  return {
    up: keys.has('ArrowUp') || keys.has('arrowup'),
    down: keys.has('ArrowDown') || keys.has('arrowdown'),
    left: keys.has('ArrowLeft') || keys.has('arrowleft'),
    right: keys.has('ArrowRight') || keys.has('arrowright'),
    fire: false,
  };
}

function moveCodesForScheme(scheme: KeyboardMoveScheme): string[] {
  if (scheme === 'wasd') return ['KeyW', 'KeyA', 'KeyS', 'KeyD'];
  if (scheme === 'tfgh') return ['KeyT', 'KeyF', 'KeyG', 'KeyH'];
  if (scheme === 'ijkl') return ['KeyI', 'KeyJ', 'KeyK', 'KeyL'];
  if (scheme === 'numpad') return ['Numpad8', 'Numpad5', 'Numpad2', 'Numpad4', 'Numpad6'];
  return ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) return !target.disabled && !target.readOnly;
  if (target instanceof HTMLSelectElement) return !target.disabled;
  return target.isContentEditable || Boolean(target.closest('[contenteditable="true"]'));
}
